import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import * as _ from 'lodash';

export interface SortRule {
    id: string;
    columnName: string;
    displayName: string;
    order: 'asc' | 'desc';
    priority: number;
    enabled: boolean;
    customSort?: (a: any, b: any) => number;
}

export interface SortOption {
    label: string;
    value: 'asc' | 'desc';
    icon: string;
}

@Injectable({
    providedIn: 'root'
})
export class AdvancedSortService {
    private availableColumnsSubject = new BehaviorSubject<any[]>([]);
    public availableColumns$ = this.availableColumnsSubject.asObservable();
    private sortRulesSubject = new BehaviorSubject<SortRule[]>([]);
    public sortRules$: Observable<SortRule[]> = this.sortRulesSubject.asObservable();

    public readonly sortOptions: SortOption[] = [
        { label: 'Ascending (A-Z, 0-9)', value: 'asc', icon: 'fa-sort-alpha-asc' },
        { label: 'Descending (Z-A, 9-0)', value: 'desc', icon: 'fa-sort-alpha-desc' }
    ];

    constructor() {}

    /**
     * Get current sort rules
     */
    getSortRules(): SortRule[] { return this.sortRulesSubject.value; }

    setAvailableColumns(columns: any[]): void { this.availableColumnsSubject.next(columns);}
    getAvailableColumns(): any[] { return this.availableColumnsSubject.value; }

    /**
     * Add a new sort rule
     */
    addSortRule(columnName: string, displayName: string, order: 'asc' | 'desc' = 'asc'): void {
        const currentRules = this.sortRulesSubject.value;
        
        // Check if rule already exists
        const existingRule = currentRules.find(r => r.columnName === columnName);
        if (existingRule) {
            // Update existing rule
            existingRule.order = order;
            existingRule.enabled = true;
            this.sortRulesSubject.next([...currentRules]);
            return;
        }

        // Add new rule
        const newRule: SortRule = {
            id: this.generateId(),
            columnName,
            displayName,
            order,
            priority: currentRules.length,
            enabled: true
        };

        this.sortRulesSubject.next([...currentRules, newRule]);
    }

    /**
     * Remove a sort rule
     */
    removeSortRule(ruleId: string): void {
        const currentRules = this.sortRulesSubject.value;
        const updatedRules = currentRules
            .filter(r => r.id !== ruleId)
            .map((r, index) => ({ ...r, priority: index })); // Reorder priorities
        
        this.sortRulesSubject.next(updatedRules);
    }

    /**
     * Toggle sort rule enabled/disabled
     */
    toggleSortRule(ruleId: string): void {
        const currentRules = this.sortRulesSubject.value;
        const rule = currentRules.find(r => r.id === ruleId);
        if (rule) {
            rule.enabled = !rule.enabled;
            this.sortRulesSubject.next([...currentRules]);
        }
    }

    /**
     * Update sort rule order
     */
    updateSortOrder(ruleId: string, order: 'asc' | 'desc'): void {
        const currentRules = this.sortRulesSubject.value;
        const rule = currentRules.find(r => r.id === ruleId);
        if (rule) {
            rule.order = order;
            this.sortRulesSubject.next([...currentRules]);
        }
    }

    /**
     * Reorder sort rules (for drag-and-drop)
     */
    reorderSortRules(newOrder: SortRule[]): void {
        const updatedRules = newOrder.map((rule, index) => ({
            ...rule,
            priority: index
        }));
        this.sortRulesSubject.next(updatedRules);
    }

    /**
     * Clear all sort rules
     */
    clearAllRules(): void {
        this.sortRulesSubject.next([]);
    }

    /**
     * Apply sorting to data array
     */
    applySorting(data: any[]): any[] {
        if (!data || data.length === 0) {
            return data;
        }

        const activeRules = this.sortRulesSubject.value
            .filter(r => r.enabled)
            .sort((a, b) => a.priority - b.priority);

        if (activeRules.length === 0) {
            return data;
        }

        // Build lodash orderBy parameters
        const iteratees = activeRules.map(rule => {
            if (rule.customSort) {
                return rule.customSort;
            }
            return rule.columnName;
        });

        const orders = activeRules.map(rule => rule.order);

        return _.orderBy(data, iteratees, orders);
    }

    /**
     * Get sort summary text
     */
    getSortSummary(): string {
        const activeRules = this.sortRulesSubject.value.filter(r => r.enabled);
        
        if (activeRules.length === 0) {
            return 'No sorting applied';
        }

        const sorted = activeRules.sort((a, b) => a.priority - b.priority);
        const summary = sorted.map((rule, index) => {
            const orderText = rule.order === 'asc' ? '?' : '?';
            return `${index + 1}. ${rule.displayName} ${orderText}`;
        }).join(', ');

        return summary;
    }

    /**
     * Generate unique ID
     */
    private generateId(): string {
        return `sort_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    }

    /**
     * Export sort configuration
     */
    exportConfig(): string {
        return JSON.stringify(this.sortRulesSubject.value, null, 2);
    }

    /**
     * Import sort configuration
     */
    importConfig(config: string): void {
        try {
            const rules = JSON.parse(config) as SortRule[];
            this.sortRulesSubject.next(rules);
        } catch (error) {
            console.error('Failed to import sort configuration:', error);
        }
    }
}
